import {
  IconCreditCard,
  IconDotsVertical,
  IconLogout,
  IconNotification,
  IconUserCircle,
} from "@tabler/icons-react";

import { Avatar } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Box, Typography } from "@mui/material";
import { CircleUserRound } from "lucide-react";
import { RolesLevels } from "@/services/types/types";
import { useNavigate } from "react-router-dom";
import { RoutesEnum } from "@/routes/enum/Routes";
import { useState } from "react";
import DialogProfile from "../DialogProfile";
import { User } from "@/interfaces/User";
import DialogPassword from "../DialogPassword";
import { api } from "@/services/instance";
import DialogCreateFeedback from "../DialogCreateFeedback";

interface NavUserProps {
  userInfo: User;
}

const NavUser = ({ userInfo }: NavUserProps) => {
  const { isMobile } = useSidebar();
  const [open, setOpen] = useState(false);
  const [openEditPassword, setOpenEditPassword] = useState(false);
  const [openCreateFeedback, setOpenCreateFeedback] = useState(false);
  const navigate = useNavigate();

  const logout = () => {
    api.post("/auth/logout", {
      withCredentials: true,
    });
    navigate(RoutesEnum.ROOT);
  };

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <Avatar className="flex h-8 w-8 rounded-lg justify-center items-center">
                  <CircleUserRound />
                </Avatar>
                <Box className="grid flex-1 text-left text-sm leading-tight">
                  <Typography
                    fontWeight={500}
                    textOverflow={"truncate"}
                    fontSize={"12px"}
                    fontFamily={"Inter"}
                  >
                    {userInfo.username}
                  </Typography>
                  <Typography
                    textOverflow={"truncate"}
                    fontSize={"10px"}
                    fontFamily={"Inter"}
                  >
                    {userInfo.email}
                  </Typography>
                </Box>
                <IconDotsVertical className="ml-auto size-4" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
              side={isMobile ? "bottom" : "right"}
              align="end"
              sideOffset={4}
            >
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <Avatar className="h-8 w-8 rounded-lg justify-center items-center">
                    <CircleUserRound />
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <Typography
                      fontWeight={500}
                      textOverflow={"truncate"}
                      fontSize={"12px"}
                      fontFamily={"Inter"}
                    >
                      {userInfo.username}
                    </Typography>
                    <Typography
                      textOverflow={"truncate"}
                      fontSize={"10px"}
                      fontFamily={"Inter"}
                    >
                      {userInfo.email}
                    </Typography>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem
                  onClick={() => setOpen((prev) => !prev)}
                  aria-label="Perfil"
                >
                  <IconUserCircle />
                  <Typography fontSize={"12px"} fontFamily={"Inter"}>
                    Perfil
                  </Typography>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setOpenEditPassword((prev) => !prev)}
                  aria-label="Cambiar contraseña"
                >
                  <IconCreditCard />
                  <Typography fontSize={"12px"} fontFamily={"Inter"}>
                    Cambiar contraseña
                  </Typography>
                </DropdownMenuItem>
                {(userInfo.role === RolesLevels.Client ||
                  userInfo.role === RolesLevels.MRM) && (
                  <DropdownMenuItem
                    onClick={() => setOpenCreateFeedback((prev) => !prev)}
                    aria-label="crear-feedback"
                  >
                    <IconNotification />
                    <Typography fontSize={"12px"} fontFamily={"Inter"}>
                      Feedback
                    </Typography>
                  </DropdownMenuItem>
                )}
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => logout()}
                aria-label="Cerrar sesión"
              >
                <IconLogout />
                <Typography fontSize={"12px"} fontFamily={"Inter"}>
                  Log out
                </Typography>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
      <DialogProfile open={open} setOpen={setOpen} userInfo={userInfo} />
      <DialogPassword
        open={openEditPassword}
        setOpen={setOpenEditPassword}
        userInfo={userInfo}
      />
      <DialogCreateFeedback
        open={openCreateFeedback}
        setOpen={setOpenCreateFeedback}
        userInfo={userInfo}
      />
    </>
  );
};

export default NavUser;
